// ============================================================================
// AFB-0075 / PR 5 — launching an onboarding packet at one person.
//
// Everything here is pure. It lives apart from envelope-lib.ts for the reason
// packet-composition.ts and template-bindings.ts do: that module imports
// pdfjs-dist at load time, which needs DOMMatrix, so a node test cannot import
// it AT ALL. The decisions this file makes are the ones that must not be wrong
// quietly, so they are the ones that get tested:
//
//   1. WHY THE BUTTON IS OFF. A disabled control with no reason on screen is
//      the same as a broken one. Every refusal here is a whole sentence that
//      names the missing fact and what to do about it.
//   2. WHICH PACKET, BY WORKER CLASS. W2 gets the W2 packet; a subcontractor
//      or partner gets the subcontractor one. Chosen by what the packet's own
//      documents say they apply to — never by matching on the packet's name.
//   3. INCLUDE / EXCLUDE FOR ONE SEND. A tick that changes nothing must send
//      NULL, not []: the plan reads "IS NOT NULL" as "the sender overrode the
//      packet" and stamps document_overrides on that same test, so an empty
//      array records an override nobody made.
//   4. THE SEND GATE. A blank on a signed legal document is worse than an
//      error message, so an unresolved required merge field BLOCKS the send
//      rather than warning about it. A RESTRICTED field (SSN, DOB, bank
//      account) is empty at send time BY DESIGN — the employee fills it in —
//      so it is never counted as a merge field with no value, or the gate
//      would refuse to send every W-4 the company has.
//   5. THE SIGNER-STATE SEEDING RULE. esign_fields.value was written by the
//      merge resolver and never read by the signer UI, so every prefilled
//      value was invisible and — because the signer's submission is written
//      over the whole row — was then blanked on submit. Seeding is the fix and
//      it lives here, tested, rather than inside a component: it has to run
//      when the fields ARRIVE (they load async) and must never overwrite what
//      the signer has already typed.
// ============================================================================

import { matchesWorkerClass } from './packet-composition';

// ── what the launcher knows about a packet ──────────────────────────────────

/** One member document of a packet, as the picker shows it. */
export interface LaunchDocument {
  link_id: string;
  template_id: string;
  /** The template's name, or its id when the template row is gone. */
  name: string;
  sort_order: number;
  is_required: boolean;
  page_count: number | null;
  /** Worker classes this document is limited to. NULL ⇒ everyone. */
  applies_to: string[] | null;
  /** Two-letter states this document is limited to. NULL ⇒ every state. */
  applies_to_states: string[] | null;
}

/** One active packet plus its member rows. */
export interface LaunchPacketOption {
  packet_id: string;
  name: string;
  is_active: boolean;
  documents: LaunchDocument[];
}

/**
 * Does this packet have anything at all for this worker class?
 *
 * Answered from the documents' own applies_to lists rather than the packet's
 * name: "W2 onboarding" is a label somebody typed, and the rules that actually
 * decide what a person receives live on the join rows.
 */
export function packetServesWorkerClass(
  packet: LaunchPacketOption,
  workerClass: string | null,
): boolean {
  return packet.documents.some((d) => matchesWorkerClass(d.applies_to, workerClass));
}

/** How many of this packet's documents NAME this class explicitly. */
function specificity(packet: LaunchPacketOption, workerClass: string | null): number {
  if (!workerClass) return 0;
  return packet.documents.filter(
    (d) => d.applies_to !== null && matchesWorkerClass(d.applies_to, workerClass),
  ).length;
}

/** Every active packet that would send this person at least one document. */
export function packetsForWorkerClass(
  packets: readonly LaunchPacketOption[],
  workerClass: string | null,
): LaunchPacketOption[] {
  return packets.filter((p) => p.is_active && packetServesWorkerClass(p, workerClass));
}

/**
 * The packet to select first.
 *
 * The most SPECIFIC match wins: a packet whose documents name this class
 * outright beats one that only carries documents everybody gets. Ties break on
 * the number of documents this person would receive, then on name, so the
 * choice is stable rather than dependent on row order.
 */
export function defaultPacketFor(
  packets: readonly LaunchPacketOption[],
  workerClass: string | null,
): LaunchPacketOption | null {
  const candidates = packetsForWorkerClass(packets, workerClass);
  if (candidates.length === 0) return null;
  return [...candidates].sort((a, b) => {
    const sa = specificity(a, workerClass);
    const sb = specificity(b, workerClass);
    if (sa !== sb) return sb - sa;
    const ma = a.documents.filter((d) => matchesWorkerClass(d.applies_to, workerClass)).length;
    const mb = b.documents.filter((d) => matchesWorkerClass(d.applies_to, workerClass)).length;
    if (ma !== mb) return mb - ma;
    return a.name.localeCompare(b.name) || a.packet_id.localeCompare(b.packet_id);
  })[0];
}

// ── why the launch button is off ────────────────────────────────────────────

/** The facts the launch action needs before it can offer to build anything. */
export interface LaunchSubject {
  /** 'employee' | 'partner' — packets are built from employee_onboarding. */
  source: string;
  employee_id: string | null;
  start_date: string | null;
  /** W2 / Partner / Subcontractor, as the checklist spells it. */
  workerClass: string | null;
  /** False when this user's role cannot write. */
  canWrite: boolean;
}

function isBlank(value: string | null | undefined): boolean {
  return (value ?? '').trim() === '';
}

/**
 * Everything standing between this person and a generated packet, one sentence
 * each. Empty array ⇒ the action is live.
 *
 * Each reason names the missing fact AND where to fix it, because this text is
 * the only explanation the screen gets to give.
 */
export function launchBlockers(
  subject: LaunchSubject,
  packets: readonly LaunchPacketOption[],
): string[] {
  const out: string[] = [];

  if (subject.source !== 'employee') {
    out.push(
      'Onboarding packets are built from the employee onboarding record, and this is a ' +
        'subcontractor company record — generate the packet from the hire’s own onboarding.',
    );
    return out;
  }

  if (!subject.canWrite) {
    out.push('Read-only — your role can view onboarding records but not send documents.');
  }
  if (isBlank(subject.employee_id)) {
    out.push(
      'No employee record is linked to this onboarding yet, so there is nobody to address ' +
        'the packet to — use “Create employee record” first.',
    );
  }
  if (isBlank(subject.start_date)) {
    out.push(
      'No start date on this onboarding. The packet fills the start date into the offer and ' +
        'payroll documents, so set one before sending.',
    );
  }
  if (packetsForWorkerClass(packets, subject.workerClass).length === 0) {
    out.push(
      isBlank(subject.workerClass)
        ? 'This person’s worker class is not recorded, so no packet can be chosen for them.'
        : `No active packet covers a ${subject.workerClass} hire, so there is nothing to send. ` +
          'Add one under Manage packets on the envelopes screen.',
    );
  }
  return out;
}

/** True only when nothing at all is outstanding. */
export function canLaunchPacket(
  subject: LaunchSubject,
  packets: readonly LaunchPacketOption[],
): boolean {
  return launchBlockers(subject, packets).length === 0;
}

// ── per-send include / exclude ──────────────────────────────────────────────

/** What travels to env_from_packet. NULL means "no override", never []. */
export interface DocumentOverride {
  include: string[] | null;
  exclude: string[] | null;
}

/** Sorted, de-duplicated, blank-free — so two equal overrides compare equal. */
function tidy(ids: readonly string[]): string[] {
  return [...new Set(ids.map((id) => id.trim()).filter((id) => id !== ''))].sort();
}

/**
 * The override for ONE SEND, computed from what is ticked against what the
 * rules chose. Never mutates the packet definition — nothing here writes.
 *
 * Both halves are null when the ticks match the rules exactly. That is not
 * tidiness: the plan treats a non-null include/exclude as "the sender
 * overrode this packet" and stamps esign_envelopes.document_overrides from the
 * same test, so sending [] would record an override that never happened.
 */
export function computeDocumentOverride(
  ruleSelected: readonly string[],
  ticked: readonly string[],
): DocumentOverride {
  const rules = new Set(tidy(ruleSelected));
  const chosen = new Set(tidy(ticked));
  const include = [...chosen].filter((id) => !rules.has(id)).sort();
  const exclude = [...rules].filter((id) => !chosen.has(id)).sort();
  return {
    include: include.length > 0 ? include : null,
    exclude: exclude.length > 0 ? exclude : null,
  };
}

/**
 * esign_envelopes.document_overrides, read back. Written as
 * { added: [...], removed: [...] } by the instantiator; anything else — a null
 * column, a stray shape — reads as "no override", which is the honest answer.
 */
export function parseDocumentOverrides(value: unknown): DocumentOverride {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { include: null, exclude: null };
  }
  const row = value as { added?: unknown; removed?: unknown };
  const list = (v: unknown): string[] | null => {
    if (!Array.isArray(v)) return null;
    const ids = tidy(v.filter((x): x is string => typeof x === 'string'));
    return ids.length > 0 ? ids : null;
  };
  return { include: list(row.added), exclude: list(row.removed) };
}

// ── the plan, as esign_packet_plan returns it ───────────────────────────────
//
// Mirrors supabase/functions/esign/packet-plan.ts. Read-only: the plan writes
// nothing, so a review screen may call it as often as it likes.

/** One entry of a template's field_layout, as the plan passes it through. */
export interface PlanLayoutField {
  page?: number | null;
  position_x?: number | null;
  position_y?: number | null;
  width?: number | null;
  height?: number | null;
  field_type?: string | null;
  field_class?: string | null;
  binding?: string | null;
  required?: boolean | null;
  role_key?: string | null;
  /**
   * RESTRICTED HR FIELD — SSN / DOB / bank account / routing. Always a SIGNER
   * field with a null binding, so it is not a merge field and never belongs in
   * the prefilled list or the send gate. See restrictedFieldNotes().
   */
  is_sensitive?: boolean | null;
}

/** One document of the plan, already in assembly order. */
export interface PlanDocument {
  template_id: string;
  name: string;
  sort_order: number;
  page_count: number;
  /** 0-based page this document starts on in the assembled packet. */
  page_offset: number;
  checklist_key: string | null;
  is_required: boolean;
  field_layout?: PlanLayoutField[] | null;
}

export interface PlanRole {
  role_key: string;
  label?: string | null;
  role?: string | null;
  signing_order?: number | null;
  auth_mode?: string | null;
}

export interface PlanBindingGap {
  binding_key: string | null;
  label?: string | null;
  reason: string;
}

export interface PlanGap {
  kind: string;
  template_id?: string | null;
  reason: string;
}

export interface PacketPlan {
  packet_id: string;
  packet_name: string | null;
  onboarding_id: string;
  employee_id: string | null;
  worker_class: string | null;
  state: string | null;
  documents: PlanDocument[];
  total_pages: number;
  roles: PlanRole[];
  values: Record<string, string>;
  binding_gaps: PlanBindingGap[];
  gaps: PlanGap[];
  email_subject: string | null;
  email_body: string | null;
  note_to_signer: string | null;
  /** True when a gap makes the packet unsendable. Nothing may be created. */
  blocking: boolean;
}

/** The template_ids the RULES chose — the baseline any tick is measured against. */
export function planSelectedTemplateIds(plan: PacketPlan | null): string[] {
  return (plan?.documents ?? []).map((d) => d.template_id);
}

/**
 * The PACKET-level gaps: no employee, no start date, no state, a member with
 * no PDF. These are what make a plan blocking, so they are what the send gate
 * takes.
 */
export function planGapSentences(plan: PacketPlan | null): string[] {
  return (plan?.gaps ?? [])
    .map((g) => g.reason)
    .filter((r) => typeof r === 'string' && r.trim() !== '');
}

/**
 * Values the resolver could not find for this person.
 *
 * Deliberately NOT part of the send gate. esign_resolve_onboarding_bindings
 * walks EVERY registered binding, whether or not any document on this packet
 * uses it, so refusing the send on these would block a packet over a value
 * nothing on it needs. A gap that a document really does use shows up as an
 * unresolved required merge field — and that does block.
 */
export function planBindingGapSentences(plan: PacketPlan | null): string[] {
  return (plan?.binding_gaps ?? [])
    .map((g) => g.reason)
    .filter((r) => typeof r === 'string' && r.trim() !== '');
}

// ── merge fields on the built envelope ──────────────────────────────────────

/** The parts of an esign_fields row this module reasons about. */
export interface EnvelopeFieldLike {
  field_id: string;
  page: number;
  position_x: number;
  position_y: number;
  required: boolean;
  /** Always NULL on a restricted field — the table's CHECK guarantees it. */
  value: string | null;
  field_type?: string;
  /** Restricted to HR and executives. Never a merge field, never prefilled. */
  is_sensitive?: boolean | null;
}

/** One prefilled value, ready to be shown and corrected before anything is sent. */
export interface MergeFieldReview {
  /** NULL when the plan expects this merge field and no envelope field matches. */
  field_id: string | null;
  binding: string | null;
  /** The binding's human label when we have one, else the binding key. */
  label: string;
  document_name: string;
  /** 1-based page in the ASSEMBLED packet. */
  page: number;
  required: boolean;
  value: string | null;
}

/**
 * Positions are stored as numerics and round-trip through JSON, so they are
 * compared with a tolerance rather than with ===. A thousandth of a page is
 * well under a millimetre — far tighter than any two real placements.
 */
const POSITION_EPSILON = 0.001;

function near(a: number | null | undefined, b: number | null | undefined): boolean {
  if (typeof a !== 'number' || typeof b !== 'number') return false;
  return Math.abs(a - b) <= POSITION_EPSILON;
}

/**
 * Pair the plan's MERGE layout entries with the envelope fields they became.
 *
 * The envelope carries no "this is a merge field" marker — esign_fields has no
 * such column — so the pairing is done the same way instantiation built them:
 * a layout entry's page plus its document's cumulative page_offset, at the same
 * normalised position. Each envelope field is claimed at most once, so two
 * merge fields sitting on top of each other cannot both resolve to one row.
 *
 * A layout entry with no matching field is REPORTED (field_id null) rather than
 * dropped: it means the envelope is missing a value it was supposed to carry,
 * and that is exactly the thing this screen exists to catch.
 */
export function mergeFieldsFromPlan(
  plan: PacketPlan | null,
  fields: readonly EnvelopeFieldLike[],
  bindingLabels: Readonly<Record<string, string>> = {},
): MergeFieldReview[] {
  if (!plan) return [];
  const claimed = new Set<string>();
  const out: MergeFieldReview[] = [];

  for (const doc of plan.documents) {
    for (const entry of doc.field_layout ?? []) {
      if (entry?.field_class !== 'merge') continue;
      // A RESTRICTED entry is a signer field by construction, so it cannot
      // reach here — the placement rules never write field_class 'merge' on
      // one. Tested and restated anyway: if a hand-edited layout ever said
      // both, this list would show it as "a merge field with no value" and the
      // send gate would refuse to send a perfectly good W-4 over a box the
      // employee is supposed to fill in themselves.
      if (entry.is_sensitive === true) continue;
      const page = (entry.page ?? 1) + (doc.page_offset ?? 0);
      const match = fields.find(
        (f) =>
          !claimed.has(f.field_id) &&
          f.is_sensitive !== true &&
          f.page === page &&
          near(f.position_x, entry.position_x) &&
          near(f.position_y, entry.position_y),
      );
      if (match) claimed.add(match.field_id);
      const binding = (entry.binding ?? '').trim() || null;
      out.push({
        field_id: match?.field_id ?? null,
        binding,
        label: (binding && bindingLabels[binding]) || binding || 'Unnamed merge field',
        document_name: doc.name,
        page,
        // The ROW is authoritative about required — a template's layout can be
        // edited after an envelope was built from it.
        required: match ? match.required : entry.required !== false,
        value: match ? match.value : null,
      });
    }
  }
  return out;
}

/** A merge field counts as filled only when it holds non-blank text. */
export function isMergeFieldResolved(field: MergeFieldReview): boolean {
  return (field.value ?? '').trim() !== '';
}

/** Every REQUIRED merge field still holding nothing. */
export function unresolvedRequiredMergeFields(
  fields: readonly MergeFieldReview[],
): MergeFieldReview[] {
  return fields.filter((f) => f.required && !isMergeFieldResolved(f));
}

// ── restricted fields on the built envelope ─────────────────────────────────

/**
 * One restricted field, described WITHOUT its answer.
 *
 * There is deliberately no `value` on this shape and no way to add one: the
 * answer lives in esign_field_secrets, the review screen does not read that
 * table, and a field that cannot carry a value cannot leak one by somebody
 * later writing `{note.value}` into the JSX.
 */
export interface RestrictedFieldNote {
  field_id: string;
  /** 1-based page in the ASSEMBLED packet. */
  page: number;
  /** The document that page falls in, or '' when the plan does not cover it. */
  document_name: string;
  required: boolean;
  field_type: string;
}

/** The document a 1-based packet page falls in, by the plan's own offsets. */
function documentNameForPage(plan: PacketPlan | null, page: number): string {
  for (const doc of plan?.documents ?? []) {
    const first = (doc.page_offset ?? 0) + 1;
    const last = first + Math.max(doc.page_count ?? 0, 1) - 1;
    if (page >= first && page <= last) return doc.name;
  }
  return '';
}

/**
 * Every restricted field on this envelope, for the "these are collected but
 * you will not see them here" note.
 *
 * Read off the ENVELOPE rows rather than the plan's layout: the row is what the
 * employee will actually be shown, and a template edited after the envelope was
 * built must not change what this says.
 */
export function restrictedFieldNotes(
  plan: PacketPlan | null,
  fields: readonly EnvelopeFieldLike[],
): RestrictedFieldNote[] {
  return fields
    .filter((f) => f.is_sensitive === true)
    .map((f) => ({
      field_id: f.field_id,
      page: f.page,
      document_name: documentNameForPage(plan, f.page),
      required: f.required,
      field_type: f.field_type ?? 'text',
    }))
    .sort((a, b) => a.page - b.page || a.field_id.localeCompare(b.field_id));
}

// ── the send gate ───────────────────────────────────────────────────────────

export interface SendGateInput {
  mergeFields: readonly MergeFieldReview[];
  /** planGapSentences(plan), plus anything env_from_packet reported unresolved. */
  gaps: readonly string[];
  recipients: ReadonlyArray<{ name: string | null; email: string }>;
  /** The sender has ticked "the name and email are right". */
  recipientsConfirmed: boolean;
}

/**
 * Everything that must be true before this packet may be sent, one sentence
 * each. Empty array ⇒ Send is allowed to be pressed.
 *
 * An unresolved required merge field is a BLOCKER and not a warning. The value
 * is missing from a document somebody is about to sign, and a blank line on an
 * executed I-9 is worse in every direction than a sender seeing a refusal.
 */
export function packetSendBlockers(input: SendGateInput): string[] {
  const out: string[] = [];

  for (const f of input.mergeFields) {
    if (f.field_id === null) {
      out.push(
        `“${f.label}” was supposed to be filled in on ${f.document_name} but no field on the ` +
          'built envelope matches it. Regenerate the packet rather than sending this one.',
      );
    }
  }
  for (const f of unresolvedRequiredMergeFields(input.mergeFields)) {
    if (f.field_id === null) continue;
    out.push(
      `“${f.label}” on ${f.document_name} (page ${f.page}) is empty. Fill it in above — it ` +
        'would otherwise go out blank on a document that gets signed.',
    );
  }
  for (const gap of input.gaps) {
    if (gap.trim() !== '') out.push(gap);
  }
  if (input.recipients.length === 0) {
    out.push('This envelope has nobody to send to yet — add a recipient first.');
  }
  for (const r of input.recipients) {
    if (isBlank(r.email)) {
      out.push(
        `${r.name?.trim() || 'A recipient'} has no email address, so the packet cannot be ` +
          'delivered to them.',
      );
    }
  }
  if (!input.recipientsConfirmed && input.recipients.length > 0) {
    out.push('Confirm the recipient’s name and email before sending.');
  }
  return out;
}

/** True only when the packet may be sent. */
export function canSendPacket(input: SendGateInput): boolean {
  return packetSendBlockers(input).length === 0;
}

// ── the signer-state seeding rule ───────────────────────────────────────────
//
// THE bug this PR exists to fix. esign_fields.value is written by the merge
// resolver at instantiation and was never read by the signing UI: the signer's
// editable state started at {} and every render read values[field_id], so a
// prefilled value was invisible. Worse, the submit path writes the signer's
// map back over the WHOLE row, so a value nobody could see was then erased —
// and a required text field with nothing in the map is rejected outright.
//
// The rule has two halves and both matter:
//
//   * SEED ON ARRIVAL. Fields load asynchronously, so this cannot happen once
//     at mount — it has to run again when the fields turn up.
//   * NEVER CLOBBER. A key already present in the signer's state is theirs,
//     including a key they deliberately emptied. Re-running the seed on every
//     re-render must therefore be a no-op, which is why an unchanged map is
//     returned BY IDENTITY: React bails out of the update and no render loop
//     can form.

/** The parts of a field this rule needs. */
export interface SeedableField {
  field_id: string;
  field_type?: string;
  value: string | null;
}

/** A checkbox is stored as the text 'true'/'false' — the signer holds a boolean. */
function seedValue(field: SeedableField): string | boolean | null {
  const raw = field.value;
  if (raw === null || raw.trim() === '') return null;
  if (field.field_type === 'checkbox') {
    const folded = raw.trim().toLowerCase();
    if (folded === 'true') return true;
    if (folded === 'false') return null; // an unticked box is not a filled field
    return raw;
  }
  return raw;
}

/**
 * Fold the fields' stored values into the signer's editable state.
 *
 * Returns `current` ITSELF when there is nothing to add, so calling this on
 * every render — or from an effect that re-runs — cannot loop.
 */
export function seedSignerValues(
  current: Readonly<Record<string, string | boolean>>,
  fields: readonly SeedableField[],
): Record<string, string | boolean> {
  let next: Record<string, string | boolean> | null = null;
  for (const field of fields) {
    // Already in the signer's hands — theirs wins, always. Includes a value
    // they cleared on purpose, which is why this tests key presence and not
    // truthiness.
    if (Object.prototype.hasOwnProperty.call(current, field.field_id)) continue;
    const seeded = seedValue(field);
    if (seeded === null) continue;
    next ??= { ...current };
    next[field.field_id] = seeded;
  }
  return next ?? (current as Record<string, string | boolean>);
}
